import React, { memo, useCallback, useEffect, useState } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMaster } from "@/context/useMaster";
import { CustomSwitch } from "@/pages/add-object/CustomSwitch";
import { Slider } from "@/components/ui/slider";
import { useMediaQuery } from "@mui/material";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { useSearchContext } from "@/context/useSearchContext";
import MultiSelectFilter from "../MultiSelectFilter";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

const filterLabels = {
  objects: {
    type: "Object Type",
    medium: "Medium",
    artistName: "Artist Name",
    creationYear: "Year",
    cities: "Object Location",
    sizes: "Size",
    movements: "Movement",
    sortOptions: "Sort by",
    priceRanges: "Price Range",
  },
  artists: {
    artistName: "Artist Name",
    locations: "Artist Location",
    sortOptions: "Sort by",
  },
  businesses: {
    artistName: "Artist Name",
    locations: "Business Location",
    sortOptions: "Sort by",
  },
  videos: {
    tags: "Video Category",
    sortOptions: "Sort by",
  },
  events: {
    objectType: "Event Category",
    locations: "Event Location",
    eventType: "Event Type",
    sortOptions: "Sort by",
  },
  writing: {
    authorName: "Author Name",
    tags: "Writing Category",
    sortOptions: "Sort by",
  },
};

const filterKeys = [
  "movements",
  "cities",
  "type",
  "locations",
  "possession",
  "registration",
  "artistName",
  "creationYear",
  "medium",
  "eventType",
  "objectType",
  "tags",
  "authorName",
  "surface",
];

const outerFilter = {
  objects: ["movements", "cities"],
  artists: ["artistName", "locations"],
  businesses: ["artistName", "locations"],
  videos: ["tags"],
  events: ["locations", "eventType", "objectType"],
  writing: ["authorName", "tags"],
};

export const ArtFilters = memo(function ArtFilters() {
  const [searchParams] = useSearchParams();
  const searchCategory = searchParams?.get("category") || "objects";
  const [sheetOpen, setSheetOpen] = useState(false);
  const [accordionState, setAccordionState] = useState<string>("0");

  const isDesktop = useMediaQuery("(min-width: 1000px)");

  const { useSchema } = useMaster();
  const { filterList, searchFilter } = useSearchContext();
  const movements = useSchema("movement");
  const citySchema = useSchema("City");

  const [filterCategories, setFilterCategories] = useState<any>({});

  // Add useEffect to update filterCategories when dependencies change
  useEffect(() => {
    const updatedCategories = {
      objects: {
        type: filterList?.objectTypes || [],
        artistName: filterList?.artistName || [],
        medium: filterList?.medium ?? [],
        creationYear: filterList?.years || [],
        sizes: filterList?.maxSize || [],
        priceRanges: [],
        sortOptions: [
          { label: "Recently added", value: "recent" },
          { label: "Price: Low to High", value: "price_asc" },
          { label: "Price: High to Low", value: "price_desc" },
          { label: "Artist: A-Z", value: "artist_asc" },
        ],
        cities: filterList?.cities || [],
        movements: filterList?.movements || [],
      },
      artists: {
        artistName: filterList?.artistName || [],
        locations: filterList?.locations || [],
        movements: filterList?.movements || [],
        sortOptions: [
          { label: "Recently added", value: "recent" },
          { label: "Artist: A-Z", value: "artist_asc" },
        ],
      },
      businesses: {
        artistName: filterList?.artistName || [],
        locations: filterList?.locations || [],
        movements: filterList?.movements || [],
        sortOptions: [
          { label: "Recently added", value: "recent" },
          { label: "Business: A-Z", value: "business_asc" },
        ],
      },
      videos: {
        tags: filterList?.tags || [],
        sortOptions: [
          { label: "Recently added", value: "recent" },
          { label: "Title: A-Z", value: "title_asc" },
        ],
      },
      events: {
        objectType: filterList?.objectTypes || [],
        locations: filterList?.locations || [],
        eventType: filterList?.eventType || [],
        sortOptions: [
          { label: "Recently added", value: "recent" },
          { label: "Date: Nearest", value: "date_asc" },
        ],
      },
      writing: {
        authorName: filterList?.authorName || [],
        tags: filterList?.tags || [],
        sortOptions: [
          { label: "Recently added", value: "recent" },
          { label: "Title: A-Z", value: "title_asc" },
        ],
      },
    };

    setFilterCategories(updatedCategories);
  }, [filterList, citySchema, movements]);

  const handleFilterChange = useCallback(
    (category: string, value: string, checked: boolean) => {
      const current = searchFilter?.filterOption?.query?.[category] ?? [];
      let updated: any[];
      if (checked) {
        updated = [...current, value];
      } else {
        updated = current.filter((v: any) => v !== value);
      }
      searchFilter.setQuery({
        [category]: updated.length > 0 ? updated : undefined,
      });
    },
    [searchFilter]
  );

  const handleSliderChange = useCallback(
    (
      categoryKey: string,
      rangeValue: { min: number; max: number },
      subCat?: string
    ) => {
      searchFilter.setQuery((prev: any) => {
        const currentFilters = prev?.[categoryKey] ?? [];
        const filterKey = subCat || "price";

        const filteredArray = currentFilters.filter(
          (filter: any) => filter.option !== filterKey
        );

        return {
          ...prev,
          [categoryKey]: [
            ...filteredArray,
            {
              option: filterKey,
              unit: "cm",
              min: rangeValue.min,
              max: rangeValue.max,
            },
          ],
        };
      });
    },
    [searchFilter]
  );

  const handleAccordionChange = (value: string) => {
    // If clicking the same item, close it
    if (value === accordionState) {
      setAccordionState("");
    } else {
      setAccordionState(value);
    }
  };

  // Helper function to determine which filter component to render
  const getFilterContent = (categoryKey: string) => {
    switch (categoryKey) {
      case "priceRanges":
        return (
          <PriceRangeFilter
            categoryKey={categoryKey}
            handleSliderChange={handleSliderChange}
          />
        );
      case "sizes":
        return (
          <SizeFilter
            categoryKey={categoryKey}
            handleSliderChange={handleSliderChange}
          />
        );
      default:
        return (
          <CheckboxFilter
            categoryKey={categoryKey}
            handleFilterChange={handleFilterChange}
            filterCategories={filterCategories[searchCategory]}
          />
        );
    }
  };

  // Main filter rendering function
  const renderFilterItems = () => {
    if (
      !searchCategory ||
      !filterLabels[searchCategory as keyof typeof filterLabels]
    ) {
      return null;
    }

    const categoryLabels =
      filterLabels[searchCategory as keyof typeof filterLabels];

    return Object.entries(categoryLabels).map(([key, label], index) => (
      <AccordionItem value={index.toString()} key={index}>
        <AccordionTrigger className="py-6 border-t border-solid border-bdr-10">
          {label}
        </AccordionTrigger>
        <AccordionContent className="pt-0 pb-6">
          {getFilterContent(key)}
        </AccordionContent>
      </AccordionItem>
    ));
  };

  const FilterScrollArea = () => {
    return (
      <ScrollArea className="h-[calc(100vh-80px)] pr-4">
        <Accordion
          type="single"
          value={accordionState}
          className="w-full"
          onValueChange={(value: string) => {
            handleAccordionChange(value);
          }}
          collapsible
        >
          {renderFilterItems()}
        </Accordion>
      </ScrollArea>
    );
  };

  return (
    <div className="w-full">
      {isDesktop ? (
        <div className="flex items-center gap-4 p-10 px-0 bg-background">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="gap-2 text-gray-60 border border-solid border-[#cfcfcf] bg-[#eeeeea] rounded-full hover:text-gray-60 hover:bg-[#e7e7e1]"
              >
                <Filter className="w-4 h-4 text-gray-60" />
                All Filters
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[300px] sm:w-[360px] px-5 py-10"
            >
              <SheetHeader>
                <div className="flex items-center justify-between mb-8">
                  <h1 className="text-base leading-[19.2px] text-gray-60 font-normal">
                    Filters
                  </h1>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-5 h-5"
                    onClick={() => setSheetOpen(false)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.3335 3.33594L16.6668 16.669"
                        stroke="#303030"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3.33319 16.669L16.6665 3.33594"
                        stroke="#303030"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                </div>
              </SheetHeader>
              <FilterScrollArea />
            </SheetContent>
          </Sheet>
          {/* temporary removed */}
          {/* <div className="flex items-center gap-2">
            <span className="text-sm text-gray-100">Objects for sale</span>
            <CustomSwitch />
          </div> */}
          {outerFilter[searchCategory as keyof typeof outerFilter]?.map(
            (key) => {
              const list = filterCategories[searchCategory]?.[key] || [];
              const labels = filterLabels[
                searchCategory as keyof typeof filterLabels
              ] as Record<string, string>;
              return (
                <MultiSelectFilter
                  key={key}
                  list={list}
                  selected={searchFilter?.filterOption?.query?.[key] ?? []}
                  setQuery={searchFilter.setQuery}
                  labelKey={key}
                  title={labels[key]}
                />
              );
            }
          )}
        </div>
      ) : (
        <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
          <DrawerTrigger asChild>
            <Button
              variant="outline"
              className="gap-2 text-gray-60 border border-solid border-[#e5e5e5] h-[52px] w-[52px] rounded-full hover:bg-[#fff]"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.50065 1.75H15.5007C16.4173 1.75 17.1673 2.5 17.1673 3.41667V5.25C17.1673 5.91667 16.7507 6.75 16.334 7.16667L12.7507 10.3333C12.2507 10.75 11.9173 11.5833 11.9173 12.25V15.8333C11.9173 16.3333 11.584 17 11.1673 17.25L10.0007 18C8.91732 18.6667 7.41732 17.9167 7.41732 16.5833V12.1667C7.41732 11.5833 7.08398 10.8333 6.75065 10.4167L3.58398 7.08333C3.16732 6.66667 2.83398 5.91667 2.83398 5.41667V3.5C2.83398 2.5 3.58398 1.75 4.50065 1.75Z"
                  stroke="#303030"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Button>
          </DrawerTrigger>
          <DrawerContent className="h-screen px-5 py-10">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-base leading-[19.2px] text-gray-60 font-normal">
                Filters
              </h1>
              <Button
                variant="ghost"
                size="icon"
                className="w-5 h-5"
                onClick={() => setSheetOpen(false)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.3335 3.33594L16.6668 16.669"
                    stroke="#303030"
                    strokeWidth="1.5"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.33319 16.669L16.6665 3.33594"
                    stroke="#303030"
                    strokeWidth="1.5"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              </Button>
            </div>
            <FilterScrollArea />
          </DrawerContent>
        </Drawer>
      )}

      <div
        className={cn(
          "flex flex-wrap gap-x-4 gap-y-3 mq450:flex-nowrap mq450:overflow-x-scroll",
          filterKeys.some((key) => searchFilter?.filterOption?.query?.[key])
            ? ""
            : "hidden"
        )}
      >
        <Button
          variant="link"
          className="text-[12px] underline uppercase font-medium"
          onClick={() => {
            const query = filterKeys.reduce((acc, key) => {
              acc[key] = undefined;
              return acc;
            }, {} as Record<string, any>);
            searchFilter.setQuery(query);
          }}
        >
          Clear Filters
        </Button>
        {filterKeys.map((filterType) => {
          const values = searchFilter?.filterOption?.query?.[
            filterType
          ] as string[];
          return values?.length
            ? values.map((item: any) => (
                <FilterButton
                  key={`${filterType}-${item}`}
                  filterType={filterType}
                  value={item}
                  onRemove={(filterType: string, value: string) => {
                    const current =
                      searchFilter?.filterOption?.query?.[filterType] ?? [];
                    const updated = current.filter((v: string) => v !== value);
                    searchFilter.setQuery({
                      [filterType]: updated.length > 0 ? updated : undefined,
                    });
                  }}
                />
              ))
            : null;
        })}
      </div>

      {/* no of the filter */}
    </div>
  );
});

const SliderFilter = React.memo(
  ({
    categoryKey,
    subCat,
    handleSliderChange,
  }: {
    categoryKey: string;
    subCat?: string;
    handleSliderChange: (
      categoryKey: string,
      rangeValues: { min: number; max: number },
      subCat?: string
    ) => void;
  }) => {
    const [rangeValues, setRangeValues] = useState({ min: 0, max: 100 });
    const handleRangeChange = (values: number[]) => {
      setRangeValues({ min: values[0], max: values[1] });
    };

    const handleInputChange = (key: string, value: string) => {
      setRangeValues((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
      handleSliderChange(categoryKey, rangeValues, subCat);
    }, [categoryKey, rangeValues, subCat]);

    return (
      <div className="mt-2 space-y-4">
        <div>
          <Slider
            min={0}
            max={100}
            step={1}
            value={[rangeValues.min, rangeValues.max]}
            onValueChange={handleRangeChange}
            className="mt-4 mb-2 h-[2px]"
            thumbSize={2}
          />
          <div className="flex justify-between">
            <p className="text-[14px] leading-[16.8px]">0</p>
            <p className="text-[14px] leading-[16.8px]">100</p>
          </div>
        </div>
        <div className="flex justify-between w-full">
          <div className="w-[48%]">
            <p className="text-xs text-gray-60">Min</p>
            {categoryKey !== "sizes" && (
              <input
                type="number"
                value={rangeValues.min}
                onChange={(e) => {
                  handleInputChange("min", e.target.value);
                }}
                className="mt-1 max-w-[84px] border-solid border border-gray-20 p-[10px_12px] text-center font-sh5"
              />
            )}
          </div>
          <div className="w-[48%] flex flex-col items-end">
            <p className="text-xs text-gray-60">Max</p>
            {categoryKey !== "sizes" && (
              <input
                type="number"
                value={rangeValues.max}
                onChange={(e) => handleInputChange("max", e.target.value)}
                className="mt-1 max-w-[84px] border-solid border border-gray-20 p-[10px_12px] text-center font-sh5"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

const CheckboxFilter = ({
  categoryKey,
  handleFilterChange,
  filterCategories,
}: {
  categoryKey: string;
  filterCategories: any;
  handleFilterChange: (
    categoryKey: string,
    value: string,
    checked: boolean
  ) => void;
}) => {
  const { searchFilter } = useSearchContext();
  return (
    <div className="space-y-6 max-h-[300px] overflow-y-auto">
      {filterCategories[categoryKey as keyof typeof filterCategories]?.map(
        (item: any) => (
          <div key={item.label} className="flex items-center space-x-3">
            <Checkbox
              className="h-5 w-5 m-0 data-[state=checked]:border-[#4F4F4F]"
              checked={searchFilter?.filterOption?.query?.[
                categoryKey
              ]?.includes(item.label)}
              onCheckedChange={(checked) => {
                handleFilterChange(categoryKey, item.label, checked as boolean);
              }}
            />
            <span className="text-base leading-[19.2px] text-gray-60">
              {item.label}
              {item.count !== undefined ? ` (${item.count})` : ""}
            </span>
          </div>
        )
      )}
    </div>
  );
};

const SizeFilter = ({
  categoryKey,
  handleSliderChange,
}: {
  categoryKey: string;
  handleSliderChange: (
    categoryKey: string,
    rangeValue: { min: number; max: number },
    subCat?: string
  ) => void;
}) => (
  <div>
    <div className="flex items-center gap-2 my-2">
      <span>cm</span>
      <CustomSwitch />
      <span>in</span>
    </div>
    <Accordion type="multiple">
      {["height", "width"].map((dimension) => (
        <AccordionItem value={dimension} key={dimension}>
          <AccordionTrigger className="py-5">
            {dimension === "height" ? "Height" : "Width"}
          </AccordionTrigger>
          <AccordionContent className="pt-0 pb-6">
            <SliderFilter
              categoryKey={categoryKey}
              subCat={dimension}
              handleSliderChange={handleSliderChange}
            />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

// Helper components for each filter type
const PriceRangeFilter = ({
  categoryKey,
  handleSliderChange,
}: {
  categoryKey: string;
  handleSliderChange: (
    categoryKey: string,
    rangeValue: { min: number; max: number },
    subCat?: string
  ) => void;
}) => (
  <SliderFilter
    categoryKey={categoryKey}
    handleSliderChange={handleSliderChange}
  />
);

const FilterButton = ({
  filterType,
  value,
  onRemove,
}: {
  filterType: string;
  value: string;
  onRemove: (filterType: string, value: string) => void;
}) => {
  return (
    <Button
      variant="outline"
      className="rounded-full bg-background border-[#696969] text-foreground hover:bg-background hover:text-foreground"
      key={value}
      onClick={() => onRemove(filterType, value)}
    >
      <div className="mt-1 mr-2">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3307 2.66675L2.66406 13.3334"
            stroke="#1D1D1D"
            strokeMiterlimit="10"
          />
          <path
            d="M2.8125 2.81323L13.1858 13.1866"
            stroke="#1D1D1D"
            strokeMiterlimit="10"
          />
        </svg>
      </div>
      <span className="capitalize text-[#696969]">{value}</span>
    </Button>
  );
};
